/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState } from "react";
import { useIntl, getLocale } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";
import { Bubble } from "tea-component";
import {
  formatAccumulation, formatFilterValues, formatPercentNum, formatNum, commonValueFormatter,
  generateCopyData, getUpAndDownStatus, getWeekDay, getDynamicLocalCnEnText, getDateVal, IsSingleDay
} from '@/pages/dashboard/utils/utils';
import { PagingQuery, SortBy, TableParamsType, columsType, metricsInfoType } from "./types";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { useWeekDayArr } from "@/pages/dashboard/utils/customHook";
import { PERIODTIMEOBJ } from "@/pages/dashboard/_config/constant";
import { IExplain } from "@/pages/dashboard/types/dashboard";

export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([])
  // 总条数
  const [total, setTotal] = useState(0)

  // 表格数据
  const [tableData, setTableData] = useState([])

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([])
  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  }
}

// 表格上传参数
export function getTableRequestParams({ gameCode, filterValue: filterVal, accumulated, sort, paging, selectType, ...resetParams }: TableParamsType) {
  const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
  const { timeType, diff, rangeTime: [startTime, endTime], metrics, selector, event, ...filters } = filterVal || {};
  const isSingle = IsSingleDay({ ...filterVal, period: PERIODTIMEOBJ[filterVal?.timeType] });
  const isHaveDate = accumulated?.includes('date');
  // 应后端要求，如果没勾选date，但是日期范围为单天的日期范围，则传true，这样可以拿到event相关的两列的值，不然这两列都为空
  const reqAccumulated = isHaveDate ? accumulated : isSingle ? [...accumulated, 'date'] : accumulated;

  const params = {
    game_code: gameCode,
    filters: formatFilterValues(filters),
    start_date: getDateVal(startTime),
    end_date: getDateVal(endTime),
    accumulation: formatAccumulation(reqAccumulated),
    page: paging.pageIndex,
    page_size: paging.pageSize,
    sorted_by: by?.replace(selectType, ''),
    desc: order === 'desc',
    ...resetParams
  }
  // 特殊逻辑处理: 夜莺游戏日流失指标new_churn_users包含churn_level筛选器,跟产品确认先固定传255
  if (metrics?.includes('new_churn_users')) {
    params.filters = {
      ...formatFilterValues(filters),
      churn_level_list: ['255'],
    }
  }
  return params;
}

export function useTableColumns(filterVal: any, hasDate = true, isSingle = false) {
  const { formatMessage } = useIntl();
  const weekDayArr = useWeekDayArr()
  const isEn = getLocale() === 'en-US'
  const { timeType } = filterVal || {}
  // 日期列, 周六日的颜色高亮,
  const DATE_COLUMN = {
    key: 'date',
    copyKey: 'date',
    header: formatMessage({ id: "common_date" }),
    width: 170,
    fixed: "left",
    align: "left",
    render(record: any) {
      return <DateCell record={record} formatMessage={formatMessage} weekDayArr={weekDayArr} hasDate={hasDate} />;
    }
  }

  // 日期列, 周六日的颜色高亮 & Sum行增加提示
  function DateCell({ record, formatMessage, weekDayArr, hasDate }: any) {
    const [isIconHover, setIsIconHover] = useState(false);
    const weekDay = getWeekDay(record.date, weekDayArr)?.slice(1, -1)
    const locale = getLocale()
    const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六'
    const showSumTip = record.date === 'Sum' || record.date === '汇总值';
    // const isSumMaxMinAgr = 

    const renderedDate = !record.date ? '-' : (!!record.type || !hasDate) ?
      (record.date.includes('~') ?
        isSingle ? moment(record.date.split('~')?.[0].trim()).format("YYYY-MM-DD") :
          record.date.split('~').map((date: string) => moment(date.trim()).format("YYYY-MM-DD")).join('~') :
        ((moment(record.date).isValid()) ?
          <>
            <span className='table-text'>{moment(record.date).format("YYYY-MM-DD")}</span>
            <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
          </> :
          record.date)  // 如果是时间类型则转为时间，不是则为sum等其他类型，直接展示
      ) : (
        <>
          <span className='table-text'>{moment(record.date).format("YYYY-MM-DD")}</span>
          <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
        </>
      );

    return (
      <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
        {renderedDate}
        <span style={{ display: 'inline-block', width: '0.4em' }}></span>
        {showSumTip && (
          <div className='port-item'>
            <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
              <Bubble
                dark
                placement="top-start"
                arrowPointAtCenter
                style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                content={formatMessage({ id: 'overview_sum_tip' })}
              >
                <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
              </Bubble>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Accumulate 列
  const getAccumulateColumns = (accumulates: string[] = [], gameCode: string | undefined, selector: any) => {
    const columns: any[] = [];
    const eventColumns = (hasDate || isSingle) ? [
      {
        key: 'event_type',
        copyKey: 'event_type',
        header: formatMessage({ id: `common_event_type` }),
        align: 'left',
        width: 200,
        render(record: any) {
          // 后端反馈，现在事件的类型中文名称都为空，所以先都用英文展示
          return record?.dimension_name?.[isEn ? 'event_type_name_en' : 'event_type_name_en'] || '-'
        }
      },
      {
        key: 'event',
        copyKey: 'event',
        header: formatMessage({ id: `common_event_name` }),
        align: 'left',
        width: 200,
        render(record: any) {
          return record?.dimension_name?.[isEn ? 'event_name_en' : 'event_name_cn'] || '-'
        }
      },
    ] : []

    accumulates.forEach((value: string, i) => {
      return columns.push({
        key: value,
        copyKey: value,
        header: formatMessage({ id: `common_${value}` }),
        align: 'left',
        width: 200,
        render(record: any) {
          return getDynamicLocalCnEnText(value, record?.[value], gameCode, getLocale(), selector)
        }
      });
    })

    return [...eventColumns, ...columns];
  }

  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (metricsKey: string[], metricsInfo: { [x: string]: any; }, explainInfo: IExplain, selectType: string) => {
    const isShowUpDown = selectType !== "value_only";
    return metricsKey.reduce((columns: columsType[], v: string, t: number) => {
      const info = metricsInfo[v];
      const { metricsConfig } = info || {};
      const { type, rainbowKey, enPercentWidth, cnPercentWidth, enWidth, cnWidth, enName, cnName, explainEn, explainCn } = metricsConfig || {};
      const name = isEn ? enName : cnName;
      const isPercent = type === 'percent';
      const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      const width = (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      const onlyValueWidth = (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      columns.push(
        {
          key: v + selectType,
          copyKey: v,
          selectType: selectType,
          header: name,
          align: "right",
          explain,
          width: isShowUpDown ? width : onlyValueWidth,
          isPercent: isPercent,
          render(record) {
            const metric = record?.metrics?.[v] || {};
            const { progressWidth, metricValue } = metric;
            return (
              <>
                <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                <MetricAndUpDown
                  metric={metricValue}
                  updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                  hasTooltip={false}
                />
              </>)
          }
        },
      );
      return columns;
    }, []);
  }
  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
  }
}

/**
 * 复制当前列，语言切换，对应当前语言的取值
 * @param data 当前行数据
 */
const getCopyRenderValues = (data: any, extendConfig?: { gameCode?: any, selector?: any }) => {
  const isEn = getLocale() === 'en-US'
  if (!extendConfig) {
    const { dimension_name } = data || {};
    if (dimension_name) {
      Object.keys(data).forEach(key => {
        const value = data[key]
        data[key] = dimension_name?.[`${key}_${isEn ? 'en' : 'cn'}`] || value
      })
    }
  } else {
    data = generateCopyData(data, { gameCode: extendConfig?.gameCode, selectorList: extendConfig.selector })
  }
  return data
}

// 表格返回数据格式整理
export function useTableDataFormat(tableData: any[], metricsInfo: metricsInfoType, filterVal: any, isCopyData?: boolean, extendConfig?: { gameCode?: any, selector?: any }) {
  const isEn = getLocale() === 'en-US';
  return useMemo(() => {
    if (!Array.isArray(tableData)) return []
    return tableData.map((data) => {
      if (isCopyData) {
        data = getCopyRenderValues(data, extendConfig)
      }
      const { metrics, ...accumulates } = data || {};
      // 指标列数据
      Object.keys(metricsInfo).forEach(v => {
        const { metricsConfig } = metricsInfo[v] || {};
        const { type, decimal, percentDecimals } = metricsConfig;
        // const key = index === 0 ? v : 'contrast_' + `${index > 1 ? index + '_' + v : v}`
        // if (!metricsInfo[key]) return; // 后端会返回全量指标的数据, 而前端页面可能只展示部分指标
        const { value, dod, sdlw, change } = metrics[v] || { value: "", dod: "", sdlw: "", change: "" }
        const isPercent = type === 'percent';
        const progressWidth = isPercent ? value * 100 : 0; // 单元格内进度条的宽度
        // const metricValue = isPercent ? formatPercentNum(value, decimals, false) : formatNum(value, false, isUserType);
        const metricValue = commonValueFormatter(type, value, decimal)
        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        const changeStatus = getUpAndDownStatus(change, false);
        const dodPercentStr = formatPercentNum(dod, percentDecimals, false)
        const sdlwPercentStr = formatPercentNum(sdlw, percentDecimals, false)
        const changePercentStr = formatPercentNum(change, percentDecimals, false)

        metrics[v] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus,
          sdlwStatus,
          changeStatus,
          dodPercentStr,
          sdlwPercentStr,
          changePercentStr,
          ...metrics[v]
        }
      })

      return {
        metrics,
        ...accumulates,
        // 用作复制操作
        event_name: accumulates?.dimension_name?.[isEn ? 'event_name_en' : 'event_name_cn'],
        event_type_name: accumulates?.dimension_name?.[isEn ? 'event_type_name_en' : 'event_type_name_cn'],
      }
    })
  }, [tableData, getLocale()])
}
// 表格返回统计值数据格式整理 (和tableData用同一个hook时, 会因为useMemo调用次数太多而崩溃)
export function useStatisticsDataFormat(statisticsData: any[], metricsInfo: metricsInfoType, filterVal: any, showStatistic: any[], isCopyData: boolean) {
  const { formatMessage } = useIntl();
  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {}
    return statisticsData.reduce((acc, data) => {
      const { date, metrics, type: statisticsType, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${statisticsType}` }) // 统计值行的数据, 日期列要展示对应的聚合标题
      // 指标列数据
      Object.keys(metricsInfo).forEach(v => {
        const { metricsConfig } = metricsInfo[v] || {};
        const { type, decimal, percentDecimals } = metricsConfig;
        const isPercent = type === 'percent';
        // const key = index === 0 ? v : 'contrast_' + `${index > 1 ? index + '_' + v : v}`
        const { value } = metrics[v] || { value: "" }
        const progressWidth = isPercent && statisticsType !== 'count' ? value * 100 : 0; // 单元格内进度条的宽度
        const metricValue = isCopyData ? value : statisticsType === 'count' ? formatNum(value, false, true) : commonValueFormatter(type, value, decimal)
        metrics[v] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus: "no",
          sdlwStatus: "no",
          changeStatus: "no",
          dodPercentStr: "",
          sdlwPercentStr: "",
          changePercentStr: "",
          ...metrics[v]
        }
      })

      acc[statisticsType] = {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: false,
        type: statisticsType,
        metrics,
        ...accumulates
      }
      return acc;
    }, {})
  }, [statisticsData, getLocale()])
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension: string, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD")
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default:
      return moment(date).format("YYYY-MM-DD");
  }
}

export const useMetricsColumns = (configData?: any) => {
  const isEn = getLocale() === 'en-US';

  const getColumn = (configData: any, isEn: boolean) => {
    const columnObj: any = {}
    configData.forEach((item: any, index: any) => {
      const { metricKey } = item || {};
      columnObj[metricKey] = {
        index: index,
        metricsConfig: item,
      }
    })
    return columnObj
  }
  // 如果后端获取不到会拿前端的配置，所以这里不用写默认columns
  return getColumn(configData, isEn)
}


